import { ElementDatetimeOptions, Label } from 'core/types/element/element_datetime'
import OptionsSeparator from '../common/OptionsSeparator'
import CheckMarkIcon from './CheckMarkIcon'

export type SelectOption = {
  value: string
  label: string
  sublabel: string
  running: boolean
  options?: SelectOption[]
}

type SelectWithGroups = {
  label: string
} & (
  | {
      options: SelectOption[]
      value?: never
      sublabel?: never
      running?: never
    }
  | {
      value: string
      sublabel: string
      running: boolean
      options?: never
    }
)

type Props = {
  styles: ElementDatetimeOptions['list']
  options: SelectOption[] | SelectWithGroups[]
  readOnly: boolean
  value?: string
  placeholder: string
  onChange: (value: string) => void
}

const List = (props: Props) => {
  const { styles, options } = props
  const isChecked = (val?: string) => val === props.value
  const placeholderSession: SelectOption = {
    label: props.placeholder,
    value: 'n/a',
    sublabel: 'n/a',
    running: false
  }

  const radioStyles = {
    border: `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`,
    borderTop: styles.border_side[0]
      ? `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`
      : 'none',
    borderRight: styles.border_side[1]
      ? `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`
      : 'none',
    borderBottom: styles.border_side[2]
      ? `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`
      : 'none',
    borderLeft: styles.border_side[3]
      ? `${styles.border_size}px ${styles.border_style} rgba(${styles.border_color})`
      : 'none',
    borderTopLeftRadius: styles.border_top_left,
    borderTopRightRadius: styles.border_top_right,
    borderBottomRightRadius: styles.border_bottom_right,
    borderBottomLeftRadius: styles.border_bottom_left
  }

  const extractScheduleStyles = (opts: Label) => ({
    lineHeight: opts.line_height,
    textTransform: opts.text_transform,
    textDecoration: opts.text_decoration,
    letterSpacing: opts.letter_spacing,
    textShadow: opts.text_shadow,
    fontFamily: opts.font_family,
    fontSize: opts.font_size,
    fontWeight: opts.font_weight
  })

  const scheduledStyles = extractScheduleStyles(styles.label)
  const timeLeftStyles = extractScheduleStyles(styles.sublabel)

  const selectedOption = () => {
    let res: any = null

    options.forEach(o => {
      if (o.options) {
        const oRes = o.options.filter(oo => isChecked(oo.value))
        if (oRes) {
          res = oRes
        }
      } else if (isChecked(o.value)) {
        res = o
      }
    })

    return res
  }

  const sessions = (() => {
    if (options.length > 0) {
      if (props.readOnly) {
        const res = selectedOption()

        return res ? [res] : []
      }

      return options
    }

    return [placeholderSession]
  })()

  const markAsChecked = (value: string) => {
    if (props.readOnly || options.length === 0) return

    props.onChange(value)
  }

  const generateOption = ({ label, sublabel, value }: SelectOption, i: number) => (
    <div
      key={i}
      style={{
        ...radioStyles,
        backgroundColor: `rgba(${isChecked(value) ? styles.selected_bg_color : styles.bg_color})`
      }}
      className={`${isChecked(value) ? 'checked' : ''} wf_reset wf_datetime_radio`}
      onClick={() => markAsChecked(value)}
    >
      <div
        className="wf_reset wf_datetime_scheduled_at"
        style={{
          ...scheduledStyles,
          color: `rgba(${isChecked(value) ? styles.selected_text_color : styles.label.text_color})`
        }}
      >
        {label}
      </div>
      <div
        className="wf_reset wf_datetime_time_left"
        style={{
          ...timeLeftStyles,
          marginTop: 4,
          color: `rgba(${isChecked(value) ? styles.selected_text_color : styles.sublabel.text_color})`
        }}
      >
        {sublabel}
      </div>
      <input
        style={{ position: 'absolute', opacity: 0, cursor: 'pointer' }}
        className="wf_reset"
        type="radio"
        name={value}
        value={value}
        defaultChecked={isChecked(value)}
      />
      {!props.readOnly && (
        <div
          className="wf_reset wf_datetime_icon_wrapper"
          style={{
            backgroundColor: isChecked(value) ? `rgba(${styles.indicator_bg_color})` : '#ffffff'
          }}
        >
          <div className="wf_reset wf_datetime_icon" style={{ opacity: isChecked(value) ? 1 : 0 }}>
            <CheckMarkIcon color={`rgba(${styles.indicator_color})`} width="9px" />
          </div>
        </div>
      )}
    </div>
  )

  return (
    <div className="wf_reset wf_datetime_list">
      {sessions.map((option, index) => {
        if (option.options) {
          const optGroupstyles = {
            textAlign: 'center',
            color: `rgba(${styles.group_heading.text_color})`,
            backgroundColor: `rgba(${styles.group_heading.bg_color})`,
            lineHeight: styles.group_heading.line_height,
            textTransform: styles.group_heading.text_transform,
            textDecoration: styles.group_heading.text_decoration,
            letterSpacing: styles.group_heading.letter_spacing,
            textShadow: styles.group_heading.text_shadow,
            fontFamily: styles.group_heading.font_family,
            fontSize: `${styles.group_heading.font_size}px`,
            fontWeight: styles.group_heading.font_weight
          }

          return (
            <div key={index}>
              <OptionsSeparator label={option.label} optionStyles={optGroupstyles} />
              {option.options.map((suboption: SelectOption, subindex: number) => generateOption(suboption, subindex))}
            </div>
          )
        }

        return generateOption(option, index)
      })}
    </div>
  )
}

export default List
